import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import './StenographWaveform.css'; 

const BAR_WIDTH = 3; 
const GAP = 3;
const PADDING_RIGHT = 100; 
const FRAME_RATE_MS = 80; 

const StenographWaveform = ({ userSpeaking, assistantSpeaking, amplitude }) => {
  const [numBars, setNumBars] = useState(90); 
  
  const [barData, setBarData] = useState(
    new Array(90).fill({ height: 3, type: 'idle' })
  );
  
  const containerRef = useRef(null);
  const dataRef = useRef(new Array(90).fill({ height: 3, type: 'idle' }));
  
  const smoothedAmplitude = useRef(0);
  const animationRef = useRef(null);
  const lastUpdateRef = useRef(0);

  const lastTypeRef = useRef('idle'); 

  useLayoutEffect(() => {
    const updateBarCount = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const usableWidth = Math.max(0, width - PADDING_RIGHT);
        const possibleBars = Math.floor(usableWidth / (BAR_WIDTH + GAP));
        const newCount = Math.max(10, possibleBars);
        setNumBars(newCount);
      }
    };

    const observer = new ResizeObserver(() => updateBarCount());
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const currentLength = dataRef.current.length;
    if (numBars > currentLength) {
      const diff = numBars - currentLength;
      const newBars = new Array(diff).fill({ height: 3, type: 'idle' });
      dataRef.current = [...newBars, ...dataRef.current];
    } else if (numBars < currentLength) {
      dataRef.current = dataRef.current.slice(currentLength - numBars);
    }
  }, [numBars]);

  useEffect(() => {
    const animate = (time) => {
      if (time - lastUpdateRef.current < FRAME_RATE_MS) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      lastUpdateRef.current = time;

      let targetInput = 0;
      let currentType = 'idle'; 
      
      if (assistantSpeaking) {
        currentType = 'assistant';
        lastTypeRef.current = 'assistant'; 

        const t = Date.now() / 180; 
        const envelope = Math.max(0, Math.sin(t) + 0.2);
        targetInput = envelope * (Math.random() * 0.5 + 0.5); 
        
      } else if (userSpeaking) {
        currentType = 'user';
        lastTypeRef.current = 'user';

        const noiseFloor = 0.02; 
        targetInput = Math.max(0, amplitude - noiseFloor);
        targetInput = targetInput * 2.0; 

      } else {
        currentType = lastTypeRef.current;
      }
      
      const currentAmp = smoothedAmplitude.current;
      
      if (targetInput > currentAmp) {
        smoothedAmplitude.current += (targetInput - currentAmp) * 0.6;
      } else {
        smoothedAmplitude.current += (targetInput - currentAmp) * 0.4;
      }

      if (smoothedAmplitude.current < 0.01) smoothedAmplitude.current = 0;

      let visualHeight = 3 + (smoothedAmplitude.current * 25);
      visualHeight = Math.min(visualHeight, 28);

      if (visualHeight <= 4) {
        currentType = 'idle';
      }

      dataRef.current.shift(); 
      
      dataRef.current.push({ 
        height: visualHeight, 
        type: currentType 
      });

      if (dataRef.current.length !== numBars) {
         if (dataRef.current.length > numBars) {
            dataRef.current = dataRef.current.slice(dataRef.current.length - numBars);
         } else {
            const diff = numBars - dataRef.current.length;
            const pad = new Array(diff).fill({ height: 3, type: 'idle' });
            dataRef.current = [...pad, ...dataRef.current];
         }
      }

      setBarData([...dataRef.current]);
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [amplitude, userSpeaking, assistantSpeaking, numBars]); 

  return (
    <div className="waveform-container" ref={containerRef}>
      {barData.map((bar, index) => (
        <div
          key={index}
          className={`waveform-bar ${bar.type}`}
          style={{ height: `${bar.height}px` }}
        />
      ))}
    </div>
  );
};

export default StenographWaveform;